<script lang="ts" setup>
  import { useBreakpoints } from '@storefront/storefront';
  import { StoreLayout, AccountLayout } from '@storefront/components';
  import type { Paginator } from '@othercommerce/domain';
  import type { Complaint } from '@storefront/resources';

  interface Props {
    complaints: Paginator<Complaint>;
  }

  defineOptions({
    layout: [StoreLayout, AccountLayout],
    inheritAttrs: false,
  });

  defineProps<Props>();

  const breakpoints = useBreakpoints();
</script>
<template>
  <AccountMobileNavigation :href="$route('web.account.show')">
    {{ $t('SHOP.ACCOUNT.HEADER.COMPLAINTS') }}
  </AccountMobileNavigation>
  <SfTile large v-if="complaints.data.length > 0" class="account-orders">
    <template #title v-if="breakpoints.lg">
      <h1 class="account__heading">
        {{ $t('SHOP.ACCOUNT.HEADER.COMPLAINTS') }}
      </h1>
    </template>
    <template #default>
      <div class="account-complaints__wrapper">
        <SfListCard v-for="complaint in complaints.data">
          <template #icons>
            <span class="account-complaints__unread" v-if="complaint.customerUnread">{{ $t('SHOP.ACCOUNT.LABEL.UNREAD_MESSAGES') }}</span>
            <SfComplaintStatus :status="complaint.status" />
          </template>
          <template #default>
            <div class="account-complaints__header">
              <h2 class="account-complaints__heading">
                {{ $t('SHOP.ACCOUNT.HEADER.COMPLAINT_NUMBER', { number: complaint.number }) }}
              </h2>
              <p class="account-complaints__date">
                <SfDateTime :value="complaint.createdAt" />
              </p>
            </div>
            <div class="account-complaints__content">
              <div>
                <strong>{{ $t('SHOP.ACCOUNT.LABEL.COMPLAINT_REASON') }}</strong>
                {{ complaint.reason }}
              </div>
              <div>
                <strong>{{ $t('SHOP.ACCOUNT.LABEL.COMPLAINT_DESCRIPTION') }}</strong>
                {{ complaint.description }}
              </div>
            </div>
          </template>
          <template #actions>
            <SfButton :href="$route('web.account.complaint.show', { complaint: complaint.id })" class="account-complaints__details">
              {{ $t('SHOP.ACTION.SHOW_DETAILS') }}
            </SfButton>
          </template>
        </SfListCard>
      </div>
    </template>
  </SfTile>
  <SfTile v-else>
    <SfDataPlaceholder>
      {{ $t('SHOP.ACCOUNT.MESSAGE.COMPLAINT_PLACEHOLDER') }}
    </SfDataPlaceholder>
  </SfTile>
  <SfPagination :pagination="complaints" />

  <RouterView />
</template>

<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .account-complaints {
    &__wrapper {
      display: flex;
      flex-direction: column;
      row-gap: 1.25rem;
    }

    &__unread {
      font-size: 0.75rem;
      font-weight: var(--font-weight-bold);
    }

    &__header {
      display: flex;
      align-items: center;
      column-gap: 0.625rem;
    }

    &__heading {
      font-size: 1.25rem;
    }

    &__content {
      display: flex;
      flex-direction: column;
      font-size: 0.875rem;
    }

    &__date {
      font-size: 0.875rem;
      color: var(--sf-account-color-dark-grey);
    }
  }

  @include media.query(lg) {
    .account-complaints {
      row-gap: 1rem;

      &__wrapper .list-card__actions {
        gap: 0.75rem;
      }
    }
  }
</style>
